import React from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  Box,
  useTheme,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
} from "@mui/material";
import CtBtn from "./CustomTool/CtBtn";
import {
  AccountBalance as AccountingIcon,
  Security as SecurityIcon,
  TrendingUp as GrowthIcon,
  Group as TeamIcon,
  Calculate as CalculateIcon,
  Receipt as InvoiceIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import AboutUsImg from "../Images/AboutUs.png";
import { styled } from "@mui/material/styles";
import BookingDialog from "./BookingDialog";

const AboutUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openBooking, setOpenBooking] = React.useState(false);

  const features = [
    {
      title: "Streamlined Accounting",
      description:
        "Automate your financial processes with our intuitive platform",
      icon: <AccountingIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Advanced Security",
      description: "Bank-level encryption to protect your financial data",
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Financial Insights",
      description: "Make data-driven decisions with comprehensive analytics",
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "Invoice Management",
      description: "Create, send, and track professional invoices effortlessly",
      icon: <InvoiceIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Expense Tracking",
      description: "Monitor cash flow and categorize expenses automatically",
      icon: <CalculateIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "Team Collaboration",
      description: "Work with your accountant or team in real-time",
      icon: <TeamIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  const team = [
    {
      name: "Robert Financial",
      role: "Founder & CEO",
      experience: "15+ years in accounting technology",
      avatar: "/static/images/avatar/1.jpg",
    },
    {
      name: "Sarah Numbers",
      role: "Lead Developer",
      experience: "Full-stack developer specializing in fintech",
      avatar: "/static/images/avatar/2.jpg",
    },
    {
      name: "Michael Calculator",
      role: "Financial Advisor",
      experience: "CPA with expertise in business finance",
      avatar: "/static/images/avatar/3.jpg",
    },
  ];

  const stats = [
    { value: "10,000+", label: "Active Users" },
    { value: "98%", label: "Customer Satisfaction" },
    { value: "24/7", label: "Support Availability" },
    { value: "50+", label: "Countries Served" },
  ];

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: (theme.vars ?? theme).palette.text.secondary,
    ...theme.applyStyles("dark", {
      backgroundColor: "#1A2027",
    }),
  }));

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
          py: { xs: 6, md: 10 },
          mb: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            justifyContent="center"
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            {/* Left Content */}
            <Grid size={{ xs: 6, md: 6 }}>
              <Chip
                label="Revolutionizing Accounting"
                color="primary"
                variant="outlined"
                sx={{ mb: 2, fontWeight: 600 }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.2rem", md: "3.2rem" },
                  lineHeight: 1.2,
                }}
              >
                Accounting Made Simple
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mb: 4,
                  lineHeight: 1.6,
                  maxWidth: 500,
                }}
              >
                Rite Account provides powerful yet intuitive accounting
                solutions to help businesses of all sizes manage their finances
                with confidence.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <CtBtn
                  variant="contained"
                  size="large"
                  onClick={() => window.location.href = "mailto:support@riteaccount.com"}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                  label="Get Started"
                />
                <CtBtn
                  variant="outlined"
                  size="large"
                  onClick={() => setOpenBooking(true)}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                  label="Schedule a Demo"
                />
              </Box>
            </Grid>

            {/* Right Content - Image */}
            <Grid size={{ xs: 6, md: 6 }}>
              <Box
                component="img"
                src={AboutUsImg}
                alt="About Us"
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mission Statement */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, mb: 1.5, color: "#1976d2" }}
            >
              Our Mission
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 1, lineHeight: 1.5 }}
            >
              At Rite Account, we believe that managing your finances should be
              straightforward, transparent, and empowering. Our mission is to
              democratize accounting technology for businesses of all sizes.
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.5 }}
            >
              Founded in 2020 by financial experts and technologists, we've
              created a platform that combines powerful accounting capabilities
              with an intuitive user experience.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {[
                "Cloud-Based",
                "Secure",
                "Automated",
                "Compliant",
                "Scalable",
                "User-Friendly",
                "Reliable",
              ].map((item, index) => (
                <Chip
                  key={index}
                  icon={<CheckIcon color="primary" />}
                  label={item}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={6} mt={6} justifyContent={"center"}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.8
                )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Why Choose Rite Account?
              </Typography>
              {[
                "Save 10+ hours on accounting each month",
                "Reduce accounting errors by 95%",
                "Access real-time financial insights",
                "Scale with your growing business",
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <CheckIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="body1">{item}</Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.8
                )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Why Businesses Trust Us?
              </Typography>
              {[
                "Bank-grade data security",
                "Trusted by 1L+ happy users",
                "Dedicated support team",
                "Regular feature updates",
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <CheckIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="body1">{item}</Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ mb: 10 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 1,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Powerful Features
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mx: "auto", lineHeight: 1.6 }}
            >
              Everything you need to manage your business finances in one place
            </Typography>
          </Box>

          <Grid container rowSpacing={6} columnSpacing={3}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    p: 1,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.background.paper,
                      0.8
                    )} 0%, ${alpha(
                      theme.palette.background.default,
                      0.6
                    )} 100%)`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: "all 0.3s ease-in-out",
                    position: "relative",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 16px 40px -10px ${alpha(
                        feature.color,
                        0.2
                      )}`,
                      borderColor: alpha(feature.color, 0.3),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      background: `linear-gradient(135deg, ${alpha(
                        feature.color,
                        0.1
                      )} 0%, transparent 100%)`,
                      color: alpha(theme.palette.text.primary, 0.7),
                      transition: "all 0.3s ease",
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} mb={1}>
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
          py: 8,
          mb: 10,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box textAlign="center">
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{ mt: 1 }}
                    variant="h5"
                    color="text.secondary"
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Team
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
          >
            Meet the experts behind Rite Account
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {team.map((member, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <Card
                elevation={2}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.background.paper,
                    0.8
                  )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: `0 16px 40px -10px ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                  },
                }}
              >
                <Avatar
                  src={member.avatar}
                  sx={{
                    width: 120,
                    height: 120,
                    mx: "auto",
                    mb: 3,
                    border: `4px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                  }}
                />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {member.name}
                </Typography>
                <Chip
                  label={member.role}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2, fontWeight: 600 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {member.experience}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="lg">
        <Paper
          elevation={4}
          sx={{
            p: 6,
            borderRadius: 4,
            textAlign: "center",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: {
                xs: "1.5rem", // small screens (≈ h5 size)
                sm: "2rem",
                md: "2.5rem", // medium+
              },
            }}
          >
            Ready to Transform Your Accounting?
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 3, mx: "auto" }}
          >
            Join thousands of businesses using Rite Account to streamline their
            finances.
          </Typography>
          <CtBtn
            variant="contained"
            size="large"
            onClick={() => setOpenBooking(true)}
            sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 600 }}
            label="Get A Free Demo"
          />
        </Paper>
      </Container>
      <BookingDialog open={openBooking} onClose={() => setOpenBooking(false)} />
    </Box>
  );
};

export default AboutUs;
